import React, { useCallback, useState } from "react";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { useAuth } from "../../AuthContext";
import { openStreamWindow } from "../../Utils";
import { fetchEventByStreamKey } from "../../http/events";
import { useFocusEffect } from "@react-navigation/native";
import { Dropdown } from "react-native-element-dropdown";
import tw from "twrnc";

import Container from "@/components/shared/AppContainer";
import AppContent from "@/components/shared/AppContent";
import Text from "@/components/shared/AppText";
import AppTitle from "@/components/shared/AppTitle";
import AppHeader from "../../shared/AppHeader";
import OnboardingButton from "@/components/shared/OnboardingButton";
import { UrlToWatch } from "@/components/shared/RtmpForm";
import BrowserStreamStudio from "@/components/streaming/BrowserStreamStudio";
import { isBrowserStreamSupported } from "@/components/streaming/capabilities";
import {
  desktopStudioLabel,
  desktopStudioPlatform,
  hasDesktopStudio,
} from "@/components/desktop/desktopApp";
import { makeUrl } from "@/components/globalUtils";
import { Ionicons } from "@expo/vector-icons";
import { t } from "@/localization";

const RTMPS_SERVER = "rtmps://go.eventlive.pro:443/live";

// Public guest broadcasting page (stream/:streamKey). Signed-out friendly —
// the stream key in the link is the credential, same trust model as the
// existing app deep link. On web it offers the same "how would you like to
// go live?" choices as the owner's HowToStream screen.
const GoLive = ({ route, navigation }) => {
  const stream_key =
    route?.params?.streamKey ||
    route?.params?.stream_key ||
    route?.params?.streamkey;

  // Multicam slot invites use {key}-CAM<n>: the event is looked up by the
  // base key, but the full slot key is what gets published.
  const camSlot = stream_key?.match(/-CAM(\d+)$/)?.[1] || null;
  const lookupKey = stream_key
    ? stream_key.replace(/-CAM\d+$/, "")
    : stream_key;

  const [event, setEvent] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const { isSignedOut } = useAuth();

  const [loadingGoLive, setLoadingGoLive] = useState(false);

  const closeGoLive = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.reset({
      index: 0,
      routes: [{ name: isSignedOut ? "SignUpSignIn" : "EventList" }],
    });
  }, [isSignedOut, navigation]);

  const delegateLink = stream_key
    ? `https://eventlive.app.link/streamFor?streamKey=${encodeURIComponent(
        stream_key
      )}&$ios_nativelink=true`
    : "https://eventlive.app.link";

  useFocusEffect(
    useCallback(() => {
      let isActive = true;
      setLoadingGoLive(true);
      setErrorMessage("");

      (async () => {
        try {
          if (!stream_key) {
            if (isActive) {
              setLoadingGoLive(false);
              setErrorMessage(t("Invalid stream invite. Missing stream key."));
            }
            return;
          }

          const fetched = await fetchEventByStreamKey(lookupKey);

          if (!isActive) return;

          if (!fetched) {
            setLoadingGoLive(false);
            setErrorMessage(t("This stream invite is invalid or expired."));
            return;
          }

          if (Platform.OS === "web" && hasDesktopStudio()) {
            // EventLive for Mac or Windows: straight to the native Studio,
            // like a phone.
            setEvent(fetched);
            setSelectedOption(desktopStudioPlatform());
            setLoadingGoLive(false);
            openStreamWindow(fetched).catch(() => {});
          } else if (Platform.OS === "web") {
            setEvent(fetched);
            setSelectedOption(isBrowserStreamSupported() ? "browser" : "app");
            setLoadingGoLive(false);
          } else {
            const streamOpened = await openStreamWindow(fetched);
            if (!isActive) return;
            if (!streamOpened) {
              setLoadingGoLive(false);
              return;
            }
            closeGoLive();
          }
        } catch (error) {
          console.error("GoLive deep-link error:", error);
          if (!isActive) return;
          setLoadingGoLive(false);
          setErrorMessage(
            t(
              "Could not start this stream. Please check the invite link and try again."
            )
          );
        }
      })();

      return () => {
        isActive = false;
      };
    }, [closeGoLive, stream_key])
  );

  // "mac" or "windows" in EventLive for Mac or Windows, else null.
  const desktopStudio = desktopStudioPlatform();
  const options = [
    ...(desktopStudio
      ? [{ label: desktopStudioLabel(), value: desktopStudio }]
      : []),
    ...(isBrowserStreamSupported() && !desktopStudio
      ? [{ label: t("This Browser"), value: "browser" }]
      : []),
    { label: t("EventLive App"), value: "app" },
    { label: t("RTMP (OBS, encoders, cameras)"), value: "rtmp" },
  ];

  const watchUrl = event ? makeUrl(event) : "";

  return (
    <Container>
      <AppHeader
        leftButtonComponent={
          <TouchableOpacity activeOpacity={1} onPress={closeGoLive}>
            <Ionicons name="close-outline" size={30} color="#F84123" />
          </TouchableOpacity>
        }
        hasTitle={false}
        hasRightButton={false}
        // No chat over the camera and stream controls.
        hasSupportButton={false}
      />

      <AppContent
        contentContainerStyle={[
          styles.content,
          loadingGoLive && styles.loadingContent,
        ]}
      >
        {loadingGoLive && (
          <View style={styles.loadingMessageWrap}>
            <Text style={styles.loadingMessage}>{t("Starting stream...")}</Text>
          </View>
        )}
        {!loadingGoLive && !!errorMessage && <Text>{errorMessage}</Text>}

        {!loadingGoLive && event && (
          <View style={tw`w-full max-w-[720px] self-center`}>
            <AppTitle
              text={
                camSlot
                  ? t('You\'re invited to stream "{title}" — Camera {number}', {
                      title: event.title,
                      number: camSlot,
                    })
                  : t('You\'re invited to stream "{title}"', {
                      title: event.title,
                    })
              }
              style={tw`text-left mb-1`}
            />
            <Text style={tw`text-gray-500 mb-2`}>
              {t("How would you like to go live?")}
            </Text>

            <Dropdown
              data={options}
              labelField="label"
              valueField="value"
              value={selectedOption}
              onChange={(item) => setSelectedOption(item.value)}
              style={tw`mt-2 mb-5 p-2.5 border border-gray-300 rounded-lg`}
              containerStyle={tw`mb-5 p-2.5 border border-gray-300 rounded-lg`}
            />

            {desktopStudio !== null && selectedOption === desktopStudio && (
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {event.is_test
                    ? t(
                        "Streaming happens in the Studio window. Pick your camera and microphone there, then press Start Test."
                      )
                    : t(
                        "Streaming happens in the Studio window. Pick your camera and microphone there, then press Start."
                      )}
                </Text>
                <OnboardingButton
                  text={t("Open Studio")}
                  icon="videocam-outline"
                  onPress={() => openStreamWindow(event)}
                />
                <UrlToWatch
                  title={t("Link to Watch")}
                  url={watchUrl}
                  isTest={!!event.is_test}
                />
              </View>
            )}

            {selectedOption === "browser" && (
              <BrowserStreamStudio
                event={event}
                streamKey={stream_key}
                isTest={Boolean(event.is_test)}
                watchUrl={watchUrl}
              />
            )}

            {selectedOption === "app" && (
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {t(
                    "Install the EventLive app, then open this invite link on your phone — it starts the stream for this event automatically."
                  )}
                </Text>
                <OnboardingButton
                  text={t("Open in EventLive App")}
                  icon="videocam-outline"
                  onPress={() => {
                    window.location.href = delegateLink;
                  }}
                />
                <UrlToWatch title={t("Link to Watch")} url={watchUrl} />
              </View>
            )}

            {selectedOption === "rtmp" && (
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {t(
                    "Use these settings in OBS, a hardware encoder or a camera:"
                  )}
                </Text>
                <UrlToWatch title={t("RTMP Server URL")} url={RTMPS_SERVER} />
                <UrlToWatch title={t("Stream Key")} url={stream_key} />
                <UrlToWatch title={t("Link to Watch")} url={watchUrl} />
              </View>
            )}
          </View>
        )}
      </AppContent>
    </Container>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 24,
    flexGrow: 1,
  },
  loadingContent: {
    justifyContent: "center",
  },
  loadingMessageWrap: {
    alignItems: "center",
  },
  loadingMessage: {
    fontSize: 28,
    fontWeight: "600",
    textAlign: "center",
  },
});

export default GoLive;
